import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Gift, Eye, EyeOff, Sparkles } from "lucide-react";
import TourHeroPlayer from "@/components/app/TourHeroPlayer";
import { useTranslation } from "react-i18next";

export default function AuthPage({ mode = "login" }) {
  const { t } = useTranslation();
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    dob: "",
    gender: "female",
    interested_in: "all",
    referral_code: "",
  });
  const [busy, setBusy] = useState(false);

  // Capture ?ref=CODE from share links (e.g. https://sparkdatin.com/register?ref=ALI-BP43)
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref && !isLogin) {
      setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
      // Persist so it survives the user navigating to /login and back
      try { sessionStorage.setItem("spark_ref", ref.toUpperCase()); } catch {}
    } else if (!isLogin) {
      try {
        const stored = sessionStorage.getItem("spark_ref");
        if (stored) setForm((f) => ({ ...f, referral_code: stored }));
      } catch {}
    }
  }, [searchParams, isLogin]);

  const computeAge = (dob) => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin) {
      const age = computeAge(form.dob);
      if (age == null || age < 18) {
        toast.error(t("auth.age_error"));
        return;
      }
    }
    setBusy(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        nav("/discover");
      } else {
        const u = await register({
          email: form.email,
          password: form.password,
          name: form.name,
          dob: form.dob,
          gender: form.gender,
          interested_in: form.interested_in,
          referral_code: form.referral_code ? form.referral_code.trim().toUpperCase() : null,
        });
        try { sessionStorage.removeItem("spark_ref"); } catch {}
        toast.success(t("auth.welcome_toast", { name: u.name }));
        nav("/onboarding");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden lg:grid lg:grid-cols-2">
      {/* Desktop-only brand panel — split-screen left half */}
      <div className="hidden lg:flex flex-col justify-center px-14 xl:px-24 bg-[#101114] border-r border-white/[0.05]">
        <Link to="/" className="inline-flex items-center gap-3 mb-8 active:scale-95 transition w-fit" data-testid="desktop-brand-link" aria-label="Back to Spark home">
          <div className="w-14 h-14 rounded-full bg-[#B95F73] flex items-center justify-center shadow-lg shadow-[#B95F73]/40">
            <Sparkles className="w-7 h-7 text-[#F0E6D2]" fill="#F0E6D2" />
          </div>
          <span className="font-display font-black text-6xl tracking-tight text-[#F0E6D2]">Spark</span>
        </Link>
        <h2 className="font-display text-4xl xl:text-5xl font-light leading-[1.08] tracking-tight mb-3">
          Editorial dating.{" "}
          <span className="font-display-italic font-medium text-[#B95F73]">Real humans only.</span>
        </h2>
        <p className="text-[#A0A0A8] text-base mb-8 max-w-md">
          Matches, group rooms, moments and calls — see the whole app in a narrated two-minute tour before you even sign in.
        </p>
        <div className="max-w-xl">
          <TourHeroPlayer />
        </div>
      </div>

      {/* Form column — full page on mobile, right half on desktop */}
      <div className="flex flex-col px-6 pt-6 pb-4 max-w-md mx-auto w-full lg:max-w-xl lg:justify-center lg:px-16 lg:pt-0 lg:pb-0 lg:h-screen lg:overflow-y-auto">
      <div className="mb-4 lg:hidden">
        <Link to="/" className="inline-flex items-center gap-2.5 active:scale-95 transition" data-testid="back-to-landing" aria-label="Back to Spark home">
          <div className="w-11 h-11 rounded-full bg-[#B95F73] flex items-center justify-center shadow-lg shadow-[#B95F73]/40">
            <Sparkles className="w-5 h-5 text-[#F0E6D2]" fill="#F0E6D2" />
          </div>
          <span className="font-display font-black text-4xl tracking-tight text-[#F0E6D2]">Spark</span>
        </Link>
      </div>

      <h1 className="font-display text-3xl lg:text-5xl font-light leading-[1.05] tracking-tight mb-1 lg:mb-2">
        {isLogin ? (
          <>
            {t("auth.welcome")}{" "}
            <span className="font-display-italic font-medium text-[#B95F73]">{t("auth.back_accent")}</span>
          </>
        ) : (
          <>
            {t("auth.make")}{" "}
            <span className="font-display-italic font-medium text-[#B95F73]">{t("auth.sparks_accent")}</span> {t("auth.fly")}
          </>
        )}
      </h1>
      <p className="text-[#A0A0A8] text-xs lg:text-base mb-3 lg:mb-6">
        {isLogin
          ? t("auth.subtitle_login")
          : t("auth.subtitle_signup")}
      </p>

      {/* Hero product-tour player — prominent above-the-fold placement so
          first-time visitors (and Google Play reviewers) can preview the
          full narrated 2-minute walkthrough BEFORE creating an account.
          On desktop it lives in the left brand panel instead. */}
      <div className="lg:hidden">
        <TourHeroPlayer />
      </div>

      <form onSubmit={submit} className="space-y-2 lg:space-y-3" data-testid="auth-form">
        {!isLogin && (
          <>
            <Field
              label={t("auth.name")}
              value={form.name}
              onChange={(v) => set("name", v)}
              testid="signup-name"
              required
            />
            <Field
              label={t("auth.dob")}
              type="date"
              value={form.dob}
              onChange={(v) => set("dob", v)}
              testid="signup-dob"
              required
            />
            <div className="grid grid-cols-2 gap-3">
              <Select
                label={t("auth.i_am")}
                value={form.gender}
                onChange={(v) => set("gender", v)}
                options={[
                  ["female", t("auth.gender_female")],
                  ["male", t("auth.gender_male")],
                  ["other", t("auth.gender_other")],
                ]}
                testid="signup-gender"
              />
              <Select
                label={t("auth.show_me")}
                value={form.interested_in}
                onChange={(v) => set("interested_in", v)}
                options={[
                  ["all", t("auth.show_everyone")],
                  ["female", t("auth.show_women")],
                  ["male", t("auth.show_men")],
                ]}
                testid="signup-interested"
              />
            </div>
          </>
        )}
        <Field
          label={t("auth.email")}
          type="email"
          value={form.email}
          onChange={(v) => set("email", v)}
          testid="auth-email"
          required
        />
        <Field
          label={t("auth.password")}
          type="password"
          value={form.password}
          onChange={(v) => set("password", v)}
          testid="auth-password"
          required
        />

        {!isLogin && (
          <ReferralField
            value={form.referral_code}
            onChange={(v) => set("referral_code", v)}
          />
        )}

        {isLogin && (
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-xs font-bold tracking-widest uppercase text-[#A0A0A8] hover:text-[#B95F73] transition"
              data-testid="login-forgot-password-link"
            >
              {t("auth.forgot_password")}
            </Link>
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-[#0E0F12] text-[#F0E6D2] rounded-full py-4 mt-4 font-bold tracking-wide active:scale-95 transition flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="auth-submit"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {isLogin ? t("auth.sign_in") : t("auth.create_account_btn")}
        </button>

        {/* Google OAuth (Emergent-managed).
            REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
            REDIRECT URLS, THIS BREAKS THE AUTH */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/[0.06]" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-[#16171A] px-3 text-[10px] uppercase tracking-[0.22em] text-[#6E6A62]">
              or
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            const redirectUrl = window.location.origin + "/auth-callback";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full bg-white text-[#16171A] rounded-full py-3.5 font-bold tracking-wide active:scale-95 transition flex items-center justify-center gap-3 hover:bg-[#F5F5F7]"
          data-testid="auth-google-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
      </form>

      <p className="text-center text-sm mt-8 text-[#A0A0A8]">
        {isLogin ? t("auth.new_here") : t("auth.already_on_spark")}{" "}
        <Link
          to={isLogin ? "/signup" : "/login"}
          className="font-bold text-[#F0E6D2] underline underline-offset-4 decoration-[#B95F73] decoration-2"
          data-testid="toggle-auth-mode"
        >
          {isLogin ? t("auth.create_an_account") : t("auth.sign_in")}
        </Link>
      </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  // Show/hide toggle for any password field — lets users catch typos instead
  // of squinting at hidden dots.
  const isPassword = type === "password";
  const [visible, setVisible] = useState(false);
  const effectiveType = isPassword && visible ? "text" : type;
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-widest uppercase text-[#A0A0A8]">{label}</span>
      <div className="relative mt-1">
        <input
          type={effectiveType}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          autoComplete={isPassword ? "current-password" : undefined}
          className={`w-full bg-[#1B1B1E] border border-[#2A2A2F] rounded-2xl px-4 py-3 text-base outline-none focus:border-[#F0E6D2] transition ${isPassword ? "pr-12" : ""}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Hide password" : "Show password"}
            data-testid={testid ? `${testid}-toggle` : "password-toggle"}
            className="absolute inset-y-0 right-3 flex items-center text-[#8C867C] hover:text-[#F0E6D2] active:scale-90 transition"
          >
            {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </label>
  );
}

function ReferralField({ value, onChange }) {
  const { t } = useTranslation();
  // Auto-open if there's already a value (e.g. from ?ref=). Otherwise it's a
  // subtle toggle so the signup form isn't cluttered for the 95% who don't
  // arrive via a referral link.
  const [open, setOpen] = React.useState(Boolean(value));
  React.useEffect(() => { if (value) setOpen(true); }, [value]);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 text-xs text-[#A0A0A8] hover:text-[#10B981] transition-colors underline underline-offset-4 decoration-dotted"
        data-testid="auth-show-referral"
      >
        <Gift className="w-3.5 h-3.5" /> {t("auth.got_invite_code")}
      </button>
    );
  }
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-widest uppercase text-[#C9A961] flex items-center gap-1.5">
        <Gift className="w-3 h-3" /> {t("auth.invite_code")} <span className="text-[#A0A0A8] font-normal normal-case tracking-normal">({t("common.optional")})</span>
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder={t("auth.invite_code_placeholder")}
        data-testid="auth-referral-code"
        className="mt-1 w-full bg-[#1B1B1E] border border-[#C9A961]/40 rounded-2xl px-4 py-3 text-base outline-none focus:border-[#C9A961] transition tracking-widest font-mono uppercase"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-widest uppercase text-[#A0A0A8]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-[#1B1B1E] border border-[#2A2A2F] rounded-2xl px-3 py-3 text-sm outline-none focus:border-[#F0E6D2]"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
